import type { ReactElement } from 'react';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';

import ImageLoader from 'components/ui/loading/ImageLoader';
import { ImageSize, ImageType } from 'enums/imageType';
import useImageLoader from 'hooks/useImageLoader';

import type { Contact, Lead, User } from 'store/client/graphql';

import { BODY_TEXT } from 'styles/color';
import { BORDER_RADIUS_200 } from 'styles/tokens';

import { renderFallbackForType } from './imagesUtils';

export const ListItemImageSize = '40px';

export const HeroImageElement = styled.div`
  min-width: 0;
  width: 100%;
`;

export const HeroImage = styled.div<{ $img?: string }>`
  ${({ $img: img }) =>
    img &&
    css`
      background-image: url('${img}');
    `}
  background-position: center;
  background-size: cover;
  height: 100%;
`;

export const ThumbnailImage = styled.div`
  border-radius: 5px;
  height: 34px;
  overflow: hidden;
  width: 34px;
`;

export const ListItemImage = styled.div`
  border-radius: ${BORDER_RADIUS_200};
  flex-shrink: 0;
  height: ${ListItemImageSize};
  overflow: hidden;
  width: ${ListItemImageSize};
`;

export const DefaultImage = styled.div`
  border-radius: ${BORDER_RADIUS_200};
  height: 60px;
  overflow: hidden;
  width: 60px;
`;

export const BadgeImage = styled.div`
  height: 30px;
  overflow: hidden;
  width: auto;
`;

const FilledImage = styled(DefaultImage)`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  overflow: hidden;
  width: 100%;

  > img {
    flex-shrink: 0;
    min-height: 100%;
    min-width: 100%;
  }
`;

export const AvatarImage = styled(DefaultImage)`
  border-radius: ${BORDER_RADIUS_200};
  height: 34px;
  width: 34px;

  svg {
    color: ${BODY_TEXT};
  }
`;

export const ImageElement = styled.img`
  height: 100%;
  width: 100%;
`;

export { FallbackIcon, FallbackImage } from './imagesUtils';

export type ImageProps =
  | ImagePropsForHeroType
  | ImagePropsWithElementFallback
  | ImagePropsWithoutFallbackSource
  | ImagePropsWithUserInfoFallback;

export interface ImagePropsForHeroType extends BaseImageProps {
  fallbackSrc?: ImagePropsWithElementFallback['fallbackSrc'] | ImagePropsWithUserInfoFallback['fallbackSrc'];
  type?: ImageType.HERO;
}

interface BaseImageProps {
  size?: ImageSize;
  src?: string;
}

interface ImagePropsWithElementFallback extends BaseImageProps {
  fallbackSrc?: ReactElement<any>;
  type?: ImageType.ICON | ImageType.PHOTO;
}

interface ImagePropsWithoutFallbackSource extends BaseImageProps {
  type?: ImageType.AVATAR | ImageType.INVENTORY_ITEM;
}

interface ImagePropsWithUserInfoFallback extends BaseImageProps {
  fallbackSrc:
    | (Optional<Pick<Contact | Lead, 'firstName' | 'lastName' | 'rgbHex'>, 'lastName'> & { deleted?: undefined })
    | Optional<Pick<User, 'deleted' | 'firstName' | 'lastName' | 'rgbHex'>, 'deleted' | 'lastName'>;
  type?: ImageType.USER;
}

const Image = ({ size, src, ...props }: ImageProps) => {
  const isLoading = useImageLoader(src);

  const imageContent = useMemo(() => {
    if (isLoading) {
      return <ImageLoader />;
    }

    if (src) {
      return props.type === ImageType.HERO ? <HeroImage $img={src} /> : <ImageElement src={src} />;
    }

    return renderFallbackForType({
      fallbackSrc: 'fallbackSrc' in props ? props.fallbackSrc : undefined,
      type: props.type,
    });
  }, [isLoading, props, src]);

  const imageProps = 'fallbackSrc' in props ? { ...props, fallbackSrc: undefined } : props;

  switch (size) {
    case ImageSize.AVATAR: {
      return <AvatarImage {...imageProps}>{imageContent}</AvatarImage>;
    }

    case ImageSize.BADGE: {
      return <BadgeImage {...imageProps}>{imageContent}</BadgeImage>;
    }

    case ImageSize.FILL: {
      return <FilledImage {...imageProps}>{imageContent}</FilledImage>;
    }

    case ImageSize.HERO: {
      return <HeroImageElement {...imageProps}>{imageContent}</HeroImageElement>;
    }

    case ImageSize.LIST_ITEM: {
      return <ListItemImage {...imageProps}>{imageContent}</ListItemImage>;
    }

    case ImageSize.THUMBNAIL: {
      return <ThumbnailImage {...imageProps}>{imageContent}</ThumbnailImage>;
    }

    default: {
      return <DefaultImage {...imageProps}>{imageContent}</DefaultImage>;
    }
  }
};

export default Image;
